import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  styled,
  Divider,
  Button,
  TextField,
  Skeleton,
  ListSubheader,
  IconButton,
} from '@mui/material';
import {
  CaretLeftIcon,
  CaretRightIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router';
import {
  updateReservationSystemSetup,
  updateHotelConfig,
  setStepStatus,
  clearAllStates,
  setErrors,
} from '../../utils/redux/slices/reservationSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useStores } from '../UserJourny/FirstUserJourney/useStores';
import { RootState } from '../../utils/redux/redux';
import { getHotelogixSetupApi } from '../../utils/apis';
import { useUser } from '../../hooks/useUser';

const reservationSystems = {
  hotels: [{ id: 'hotelogix', name: 'Hotelogix' }],
  restaurants: [{ id: 'reservego', name: 'ReserveGo' }],
};

const StyledSelect = styled(Select)({
  '& .MuiSelect-select': {
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E5E7EB',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#B2B7C2',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#2563EB',
  },
});

const StyledFormControl = styled(FormControl)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '50%',
});

interface ReservationSetupProps {
  onNext: () => void;
}

interface ReservationSystemSetupData {
  restaurantId: string;
  storeId: number;
  reservationSystem: string;
  storeAlias: string;
}

interface HotelConfig {
  hotelId: string;
  hotelName?: string;
  apiUsername: string;
  counterId: string;
  consumerKey: string;
  consumerSecret: string;
  baseUrl: string;
}

const ReservationSetup = ({ onNext }: ReservationSetupProps) => {
  const navigate = useNavigate();
  const [selectedSystem, setSelectedSystem] = useState<string>('');
  const [restaurantIds, setRestaurantIds] = useState<Record<string, string>>(
    {}
  );
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {}
  );
  const [showErrors, setShowErrors] = useState(false);
  const [showPasswords, setShowPasswords] = useState<
    Record<string, Record<string, boolean>>
  >({});

  const dispatch = useDispatch();
  const { storesList, storesListLoading } = useStores();
  const { user } = useUser();

  // Get saved data from Redux
  const { reservationSystemSetup, errors } = useSelector(
    (state: RootState) => state.reservation
  );

  const savedReservationData = reservationSystemSetup.data.systems;
  const savedHotelConfigs = reservationSystemSetup.data.hotelConfigs || {};

  // Initialize state from Redux when component mounts
  useEffect(() => {
    if (savedReservationData?.length > 0) {
      const savedSystem =
        reservationSystemSetup.data.selectedSystem ||
        savedReservationData[0].reservationSystem;
      setSelectedSystem(savedSystem);
      const savedIds = savedReservationData.reduce(
        (acc: Record<string, string>, curr: ReservationSystemSetupData) => {
          acc[curr.storeId] = curr.restaurantId;
          return acc;
        },
        {}
      );
      setRestaurantIds(savedIds);
    }
  }, [savedReservationData, reservationSystemSetup.data.selectedSystem]);

  // Fetch existing hotel configurations for all stores
  useEffect(() => {
    const fetchExistingConfigs = async () => {
      if (
        selectedSystem === 'hotelogix' &&
        storesList?.length > 0 &&
        user?.tenantName
      ) {
        for (const store of storesList) {
          try {
            const response = await getHotelogixSetupApi(
              store.id,
              user.tenantName
            );
            if (response?.data?.data?.hotelConfig) {
              const hotelConfig = response.data.data.hotelConfig;

              // Populate hotel configuration in Redux
              dispatch(
                updateHotelConfig({
                  storeId: store.id,
                  config: {
                    hotelId: hotelConfig.hotelId || '',
                    hotelName: hotelConfig.hotelName || '',
                    apiUsername: hotelConfig.apiUsername || '',
                    counterId: hotelConfig.counterId || '',
                    consumerKey: hotelConfig.consumerKey || '',
                    consumerSecret: hotelConfig.consumerSecret || '',
                    baseUrl:
                      hotelConfig.baseUrl ||
                      'https://crs.hotelogix.net/ws/web/',
                  },
                })
              );

              // Populate Hotel Code field with hotelId if it exists
              if (hotelConfig.hotelId) {
                setRestaurantIds(prev => ({
                  ...prev,
                  [store.id]: hotelConfig.hotelId,
                }));
              }
            }
          } catch (error) {
            console.error(
              `Error fetching hotel config for store ${store.id}:`,
              error
            );
          }
        }
      }
    };

    fetchExistingConfigs();
  }, [selectedSystem, storesList, user?.tenantName, dispatch]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const newSystem = event.target.value;
    setSelectedSystem(newSystem);
    dispatch(updateReservationSystemSetup({ selectedSystem: newSystem }));
  };

  const handleRestaurantIdChange = (storeId: string, value: string) => {
    setRestaurantIds(prev => ({
      ...prev,
      [storeId]: value,
    }));
    setTouchedFields(prev => ({
      ...prev,
      [storeId]: true,
    }));
  };

  const handleHotelConfigChange = (
    storeId: string,
    field: keyof HotelConfig,
    value: string | number | boolean
  ) => {
    dispatch(
      updateHotelConfig({
        storeId,
        config: { [field]: value },
      })
    );

    // Clear errors for this field
    if (errors[`${storeId}_${field}`]) {
      const newErrors = { ...errors };
      delete newErrors[`${storeId}_${field}`];
      dispatch(setErrors(newErrors));
    }
  };

  const togglePasswordVisibility = (storeId: string, field: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [storeId]: {
        ...prev[storeId],
        [field]: !prev[storeId]?.[field],
      },
    }));
  };

  const validateHotelConfig = (storeId: string, config: HotelConfig) => {
    const requiredFields = [
      'hotelId',
      'apiUsername',
      'consumerKey',
      'consumerSecret',
    ];
    const newErrors: Record<string, string> = {};

    requiredFields.forEach(field => {
      const value = config[field as keyof HotelConfig];
      if (
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '')
      ) {
        newErrors[`${storeId}_${field}`] =
          `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
      }
    });

    return newErrors;
  };

  const handleNext = () => {
    if (!selectedSystem) return;

    setShowErrors(true);

    const systemsData: ReservationSystemSetupData[] = storesList
      .filter((store: any) => restaurantIds[store.id])
      .map((store: any) => ({
        restaurantId: restaurantIds[store.id],
        storeId: store.id,
        reservationSystem: selectedSystem,
        storeAlias: store.friendly_name,
      }));

    // Validate hotel configs if Hotelogix is selected
    let validationErrors: Record<string, string> = {};
    if (selectedSystem === 'hotelogix') {
      systemsData.forEach(system => {
        const config = savedHotelConfigs[system.storeId];
        if (config) {
          const configErrors = validateHotelConfig(
            String(system.storeId),
            config
          );
          validationErrors = { ...validationErrors, ...configErrors };
        }
      });
    }

    if (Object.keys(validationErrors).length > 0) {
      dispatch(setErrors(validationErrors));
      return;
    }

    dispatch(
      updateReservationSystemSetup({
        systems: systemsData,
        selectedSystem,
      })
    );

    dispatch(
      setStepStatus({
        step: 'reservationSystemSetup',
        status: {
          isComplete: systemsData.length > 0,
          hasError: systemsData.length === 0,
        },
      })
    );

    if (systemsData.length > 0) {
      onNext();
    }
  };

  const renderHotelConfigFields = (store: any) => {
    const storeId = String(store.id);
    const config = savedHotelConfigs[storeId] || {
      hotelId: '',
      hotelName: '',
      apiUsername: '',
      counterId: '',
      consumerKey: '',
      consumerSecret: '',
      baseUrl: 'https://crs.hotelogix.net/ws/web/',
    };

    return (
      <Box
        sx={{ mt: 2, p: 2, border: '1px solid #E5E7EB', borderRadius: '8px' }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 2, fontWeight: 600, fontSize: '14px' }}
        >
          Hotel Configuration for {store.friendly_name}
        </Typography>

        {/* Basic Configuration */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, fontWeight: 600, fontSize: '12px' }}
          >
            Basic Configuration
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0, fontSize: '12px' }}
            >
              Hotel ID
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter Hotel ID (e.g., 14095)"
              size="small"
              value={config.hotelId}
              onChange={e =>
                handleHotelConfigChange(storeId, 'hotelId', e.target.value)
              }
              error={!!errors[`${storeId}_hotelId`]}
              helperText={errors[`${storeId}_hotelId`]}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '12px',
                },
                '& > div': {
                  height: '40px',
                },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0, fontSize: '12px' }}
            >
              Hotel Name
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter Hotel Name"
              size="small"
              value={config.hotelName || ''}
              onChange={e =>
                handleHotelConfigChange(storeId, 'hotelName', e.target.value)
              }
              error={!!errors[`${storeId}_hotelName`]}
              helperText={errors[`${storeId}_hotelName`]}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '12px',
                },
                '& > div': {
                  height: '40px',
                },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0, fontSize: '12px' }}
            >
              API Username
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter API Username"
              size="small"
              value={config.apiUsername}
              onChange={e =>
                handleHotelConfigChange(storeId, 'apiUsername', e.target.value)
              }
              error={!!errors[`${storeId}_apiUsername`]}
              helperText={errors[`${storeId}_apiUsername`]}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '12px',
                },
                '& > div': {
                  height: '40px',
                },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0, fontSize: '12px' }}
            >
              Counter ID
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter Counter ID"
              size="small"
              value={config.counterId}
              onChange={e =>
                handleHotelConfigChange(storeId, 'counterId', e.target.value)
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '12px',
                },
                '& > div': {
                  height: '40px',
                },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0, fontSize: '12px' }}
            >
              Consumer Key
            </Typography>
            <Box sx={{ flex: 1, position: 'relative' }}>
              <TextField
                fullWidth
                placeholder="Enter Consumer Key"
                size="small"
                type={showPasswords[storeId]?.consumerKey ? 'text' : 'password'}
                value={config.consumerKey}
                onChange={e =>
                  handleHotelConfigChange(
                    storeId,
                    'consumerKey',
                    e.target.value
                  )
                }
                error={!!errors[`${storeId}_consumerKey`]}
                helperText={errors[`${storeId}_consumerKey`]}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    fontSize: '12px',
                  },
                  '& > div': {
                    height: '40px',
                  },
                }}
              />
              <IconButton
                onClick={() => togglePasswordVisibility(storeId, 'consumerKey')}
                size="small"
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'text.secondary',
                }}
              >
                {showPasswords[storeId]?.consumerKey ? (
                  <EyeSlashIcon size={16} />
                ) : (
                  <EyeIcon size={16} />
                )}
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0, fontSize: '12px' }}
            >
              Consumer Secret
            </Typography>
            <Box sx={{ flex: 1, position: 'relative' }}>
              <TextField
                fullWidth
                placeholder="Enter Consumer Secret"
                size="small"
                type={
                  showPasswords[storeId]?.consumerSecret ? 'text' : 'password'
                }
                value={config.consumerSecret}
                onChange={e =>
                  handleHotelConfigChange(
                    storeId,
                    'consumerSecret',
                    e.target.value
                  )
                }
                error={!!errors[`${storeId}_consumerSecret`]}
                helperText={errors[`${storeId}_consumerSecret`]}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    fontSize: '12px',
                  },
                  '& > div': {
                    height: '40px',
                  },
                }}
              />
              <IconButton
                onClick={() =>
                  togglePasswordVisibility(storeId, 'consumerSecret')
                }
                size="small"
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'text.secondary',
                }}
              >
                {showPasswords[storeId]?.consumerSecret ? (
                  <EyeSlashIcon size={16} />
                ) : (
                  <EyeIcon size={16} />
                )}
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '120px', flexShrink: 0, fontSize: '12px' }}
            >
              Base URL
            </Typography>
            <TextField
              fullWidth
              placeholder="https://crs.hotelogix.net/ws/web/"
              size="small"
              value={config.baseUrl}
              onChange={e =>
                handleHotelConfigChange(storeId, 'baseUrl', e.target.value)
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '12px',
                },
                '& > div': {
                  height: '40px',
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  };

  const renderStoreInputs = () => {
    if (storesListLoading) {
      return Array(2)
        .fill(0)
        .map((_, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: { xs: 'wrap', md: 'nowrap' },
            }}
          >
            <Skeleton
              variant="rectangular"
              sx={{
                width: '50%',
                height: '40px',
                borderRadius: '6px',
              }}
            />
            <Skeleton
              variant="rectangular"
              sx={{
                width: '48%',
                height: '40px',
                borderRadius: '6px',
              }}
            />
          </Box>
        ));
    }

    return storesList?.map((store: any) => (
      <Box key={store.id}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: { xs: 'wrap', md: 'nowrap' },
          }}
        >
          <TextField
            placeholder="Store Alias"
            size="small"
            value={store?.friendly_name}
            disabled
            sx={{
              width: '50%',
              flexShrink: 0,
              fontSize: '12px',
              bgcolor: 'white',
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                height: '40px',
                '& input': {
                  fontSize: '14px',
                  padding: '8px 12px',
                },
              },
            }}
          />
          <TextField
            placeholder={
              selectedSystem === 'reservego' ? 'Restaurant ID' : 'Hotel Code'
            }
            size="small"
            value={restaurantIds[store.id] || ''}
            onChange={e => handleRestaurantIdChange(store.id, e.target.value)}
            error={Boolean(
              (showErrors || touchedFields[store.id]) &&
                selectedSystem &&
                !restaurantIds[store.id]
            )}
            helperText={
              (showErrors || touchedFields[store.id]) &&
              selectedSystem &&
              !restaurantIds[store.id]
                ? 'Restaurant ID is required'
                : ''
            }
            sx={{
              width: '48%',
              flexShrink: 0,
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                height: '40px',
                '& input': {
                  fontSize: '14px',
                  padding: '8px 12px',
                },
              },
            }}
          />
        </Box>

        {/* Show hotel configuration fields for Hotelogix and if restaurant ID is filled */}
        {selectedSystem === 'hotelogix' &&
          restaurantIds[store.id] &&
          renderHotelConfigFields(store)}
      </Box>
    ));
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        backgroundColor: 'white',
        '&::-webkit-scrollbar': { display: 'none' },
        overflowY: 'auto',
      }}
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Link your existing reservation system to enable Quibble AI to manage
          bookings automatically
        </Typography>

        <Divider sx={{ margin: '24px 0' }} />

        <StyledFormControl>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: '#6B6F80',
              maxWidth: '200px',
              flexShrink: 0,
            }}
          >
            Select your Reservation Management System
          </Typography>
          <StyledSelect
            value={selectedSystem}
            onChange={(event: SelectChangeEvent<unknown>) => {
              handleChange(event as SelectChangeEvent<string>);
            }}
            displayEmpty
            sx={{ flex: 1 }}
          >
            <MenuItem disabled value="">
              <div style={{ color: '#6B6F80' }}>
                Reservation Management System
              </div>
            </MenuItem>
            <ListSubheader>Hotels</ListSubheader>
            {reservationSystems?.hotels?.map(system => (
              <MenuItem
                key={system.id}
                value={system.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span>{system.name}</span>
              </MenuItem>
            ))}
            <ListSubheader>Restaurants</ListSubheader>
            {reservationSystems?.restaurants?.map(system => (
              <MenuItem
                key={system.id}
                value={system.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span>{system.name}</span>
              </MenuItem>
            ))}
          </StyledSelect>
        </StyledFormControl>

        {selectedSystem && (
          <Box sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, minWidth: '190px', flexShrink: 0 }}
              >
                {selectedSystem === 'reservego'
                  ? 'Restaurant ID'
                  : 'Hotel Code'}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: '12px', mb: 2 }}
              >
                {selectedSystem === 'reservego'
                  ? `You can find your Restaurant ID under Settings > API Integration`
                  : `You can find your Hotel Code in the Hotelogix Account`}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '100%',
                flexShrink: 0,
              }}
            >
              {renderStoreInputs()}
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 'auto',
          p: 1,
          borderTop: '1px solid',
          borderColor: 'lightGray.main',
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
        }}
      >
        <Button
          variant="outlined"
          startIcon={<CaretLeftIcon size={16} />}
          onClick={() => {
            dispatch(clearAllStates());
            navigate('/add-new-assistant');
          }}
          sx={{
            height: '34px',
            border: '1px solid',
            borderColor: 'lightGray.main',
            borderRadius: '8px',
            color: 'custom.textBlue',
            backgroundColor: 'white',
            boxShadow: 'none',
            textTransform: 'none',
          }}
        >
          Prev
        </Button>
        <Button
          variant="contained"
          endIcon={<CaretRightIcon size={16} />}
          onClick={handleNext}
          disabled={!selectedSystem || !Object.keys(restaurantIds).length}
          sx={{
            height: '34px',
            borderRadius: '8px',
            backgroundColor: 'primary.main',
            color: 'white',
            boxShadow: 'none',
            textTransform: 'none',
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default ReservationSetup;
